import { useMemo, useState } from 'react'
import { FilterChip } from './chip'
import { Icon } from './icon'
import { Popover } from './popover'

type Item = { id: string; name: string }

// Once a chip's list passes this length, scrolling it gets painful — show a
// search box and filter client-side.
const SEARCH_THRESHOLD = 10

function useChipSearch(items: Item[]) {
  const [q, setQ] = useState('')
  const showSearch = items.length > SEARCH_THRESHOLD
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase()
    return ql ? items.filter((i) => i.name.toLowerCase().includes(ql)) : items
  }, [items, q])
  return { q, setQ, showSearch, filtered }
}

function ChipSearch({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ padding: 8, borderBottom: '1px solid var(--border-default)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '0 8px',
          height: 30,
          borderRadius: 'var(--radius-md)',
          background: 'var(--bg-3)',
          border: '1px solid var(--border-default)',
        }}
      >
        <Icon name="search" size={14} color="var(--fg-4)" />
        <input
          autoFocus
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Пошук…"
          style={{
            flex: 1,
            border: 0,
            outline: 'none',
            background: 'transparent',
            color: 'var(--fg-1)',
            fontSize: 13,
            fontFamily: 'inherit',
            minWidth: 0,
          }}
        />
      </div>
    </div>
  )
}

type MultiProps = {
  label: string
  items: Item[]
  selectedIds: string[]
  onChange: (ids: string[]) => void
  width?: number
}

export function MultiSelectChip({ label, items, selectedIds, onChange, width = 240 }: MultiProps) {
  const [open, setOpen] = useState(false)
  const { q, setQ, showSearch, filtered } = useChipSearch(items)
  const selectedSet = new Set(selectedIds)
  const summary =
    selectedIds.length === 0
      ? undefined
      : selectedIds.length === 1
        ? (items.find((i) => i.id === selectedIds[0])?.name ?? '1 обрано')
        : `${selectedIds.length} обрано`

  const toggle = (id: string) => {
    const next = new Set(selectedSet)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange([...next])
  }

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <FilterChip
        label={label}
        value={summary}
        onClick={() => setOpen(!open)}
        onClear={selectedIds.length > 0 ? () => onChange([]) : undefined}
      />
      <Popover open={open} onClose={() => setOpen(false)} width={width}>
        {showSearch && <ChipSearch value={q} onChange={setQ} />}
        <div style={{ maxHeight: 280, overflowY: 'auto' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 12, fontSize: 11, color: 'var(--fg-3)' }}>
              {items.length === 0 ? 'Немає опцій' : 'Нічого не знайдено'}
            </div>
          ) : (
            filtered.map((item) => (
              <label
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  fontSize: 12,
                  cursor: 'pointer',
                  color: 'var(--fg-1)',
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedSet.has(item.id)}
                  onChange={() => toggle(item.id)}
                />
                {item.name}
              </label>
            ))
          )}
        </div>
      </Popover>
    </span>
  )
}

type SingleProps = {
  label: string
  items: Item[]
  selectedId: string | null
  onChange: (id: string | null) => void
  width?: number
}

export function SelectChip({ label, items, selectedId, onChange, width = 240 }: SingleProps) {
  const [open, setOpen] = useState(false)
  const { q, setQ, showSearch, filtered } = useChipSearch(items)
  const summary = selectedId ? items.find((i) => i.id === selectedId)?.name : undefined

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <FilterChip
        label={label}
        value={summary}
        onClick={() => setOpen(!open)}
        onClear={selectedId ? () => onChange(null) : undefined}
      />
      <Popover open={open} onClose={() => setOpen(false)} width={width}>
        {showSearch && <ChipSearch value={q} onChange={setQ} />}
        <div style={{ maxHeight: 280, overflowY: 'auto' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 12, fontSize: 11, color: 'var(--fg-3)' }}>
              {items.length === 0 ? 'Немає опцій' : 'Нічого не знайдено'}
            </div>
          ) : (
            filtered.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  onChange(item.id)
                  setOpen(false)
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px 12px',
                  textAlign: 'left',
                  fontSize: 12,
                  background: selectedId === item.id ? 'var(--bg-3)' : 'transparent',
                  border: 0,
                  color: 'var(--fg-1)',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {item.name}
              </button>
            ))
          )}
        </div>
      </Popover>
    </span>
  )
}
